import { ButtonType, ButtonWidget, TabStyles, TextLinkWidget } from '@tableau/widgets';
import React from 'react';
import { useCallback, useState } from 'react';
import { style } from 'typestyle';
import { SessionServerSelectors, SessionUserSelectors } from '../../../../app/selectors/SessionInfoSelectors';
import { Messages } from '../../../../codegen/Localize';
import { FeatureFlags } from '../../../../ts/FeatureFlags';
import { UserSettingsActions } from '../../../../userSettings/services/UserSettingsActions';
import { SettingsSection } from '../../../components/common/SettingsSection';
import { disabledText } from '../../../styles/CommonStyles';
import { useSelector } from '../../utils';
import { ServerSettingsProps } from '../server-settings/ReactServerSettings';
import { serverSettingsForSiteAdmin, SiteSettingsForSiteAdminProps } from '../site-settings/ReactSiteSettingsForSiteAdmin';
import { componentRegistryEntry } from '../SettingsComponentRegistryFunctions';

const spacerStyle = style({
  paddingBottom: TabStyles.Spacing.MediumSpace
});

/**
 * StartPageSection appears in both server settings and site settings, but with slightly different UI.
 * Therefore there are two versions of this section: ServerStartPageSection and SiteStartPageSection.
 */
export const SERVER_FALLBACK_START_PAGE_URL = '/';
export const SERVER_START_PAGE_SECTION_TEST_ID = 'reset-default-server-start-page-setting';
export const SERVER_START_PAGE_RESET_BUTTON_TEST_ID = 'reset-server-default-start-page';
export type ServerStartPageSectionProps = ServerSettingsProps<'defaultStartPage'>;

export const SITE_START_PAGE_SECTION_TEST_ID = 'reset-default-site-start-page-setting';
export const SITE_START_PAGE_RESET_BUTTON_TEST_ID = 'reset-default-site-start-page';
export type SiteStartPageSectionProps = SiteSettingsForSiteAdminProps<'siteStartPageUri'> & serverSettingsForSiteAdmin;

export function ServerStartPageSection(props: ServerStartPageSectionProps) {
  const [serverStartPageUrl, setServerStartPageUrl ] = useState(props.settings.defaultStartPage);

  const openResetStartPageDialog = useCallback(() => {
    UserSettingsActions.confirmResetServerDefaultStartPage((newStartPage: string) => {
      setServerStartPageUrl(newStartPage);
    });
  }, [setServerStartPageUrl]);

  return (
    <StartPageSectionInternal
      sectionDescription={Messages.settings_serverStartPage_description()}
      sectionTestId={SERVER_START_PAGE_SECTION_TEST_ID}
      startPageUrl={serverStartPageUrl || SERVER_FALLBACK_START_PAGE_URL}
      isFallbackUrl={!serverStartPageUrl || serverStartPageUrl === SERVER_FALLBACK_START_PAGE_URL}
      resetButtonTestId={SERVER_START_PAGE_RESET_BUTTON_TEST_ID}
      resetButtonClickHandler={openResetStartPageDialog}
    />
  );
}

export function SiteStartPageSection(props: SiteStartPageSectionProps) {
  const [siteStartPageUrl, setSiteStartPageUrl ] = useState(props.settings.siteStartPageUri);
  const isServerAdmin = useSelector(SessionUserSelectors.isSessionServerAdmin);

  const openResetStartPageDialog = useCallback(() => {
    UserSettingsActions.confirmResetSiteDefaultStartPage(
      () => {
        setSiteStartPageUrl(null);
      },
      isServerAdmin ? props.siteId : undefined
    );
  }, [setSiteStartPageUrl]);

  const isOnlineEnvironment = useSelector(SessionServerSelectors.isOnlineEnvironment);
  const showSiteStartPage = useShowSiteStartPage(isOnlineEnvironment);

  return !showSiteStartPage ? null : (
    <StartPageSectionInternal
      sectionDescription={isOnlineEnvironment
        ? Messages.settings_siteStartPage_description_online()
        : Messages.settings_siteStartPage_description_server()}
      sectionTestId={SITE_START_PAGE_SECTION_TEST_ID}
      startPageUrl={siteStartPageUrl || props.serverSettings?.defaultStartPage || SERVER_FALLBACK_START_PAGE_URL}
      isFallbackUrl={!siteStartPageUrl}
      resetButtonTestId={SITE_START_PAGE_RESET_BUTTON_TEST_ID}
      resetButtonClickHandler={openResetStartPageDialog}
    />
  );
}

type StartPageSectionInternalProps = {
  sectionDescription: string;
  sectionTestId: string;
  startPageUrl: string;
  isFallbackUrl: boolean;
  resetButtonTestId: string;
  resetButtonClickHandler: () => void;
};

export function StartPageSectionInternal(props: StartPageSectionInternalProps) {
  return (
    <SettingsSection title={Messages.settings_startPage_title()} description={props.sectionDescription} testId={props.sectionTestId}>
      <div className={spacerStyle} id='current-start-page-id'>
        <span>{Messages.settings_startPage_current_start_page()}</span>
        <TextLinkWidget
          href={`/#${props.startPageUrl}`}
          children={props.startPageUrl}
          testId={'default-start-page-link'}
          target='_blank'
        />
        {props.isFallbackUrl &&
        <span className={disabledText} data-tb-test-id={'default-start-page-aside'}>
          &nbsp;{Messages.settings_startPage_system_default_aside()}
        </span>}
      </div>
      <ButtonWidget
        buttonType={ButtonType.Outline}
        testId={props.resetButtonTestId}
        disabled={props.isFallbackUrl}
        handleClick={props.resetButtonClickHandler}
        children={Messages.settings_startPage_reset_button()}
      />
    </SettingsSection>
  );
}

function useShowSiteStartPage(isOnlineEnvironment: boolean): boolean {
  const isSessionMultiSite = useSelector<boolean>(SessionUserSelectors.isSessionMultiSite);
  return FeatureFlags.siteDefaultStartPage && (isSessionMultiSite || isOnlineEnvironment);
}

/* for registering the component in settings registry */
export const ServerStartPageSpecs = componentRegistryEntry({
  name: 'ServerStartPageSection',
  component: ServerStartPageSection
});

export const SiteStartPageSpecs = componentRegistryEntry({
  name: 'SiteStartPageSection',
  component: SiteStartPageSection
});
